import { useState } from "react";
import { Link2, Check, Twitter, Facebook } from "lucide-react";

const SITE_URL = "https://fostercareawareness365.com";

/**
 * Minimal share bar — matches site iconography.
 * Pulls per-profile metadata (already in <head> via SEO/Helmet) so each
 * shared link renders its unique OG card on Twitter/Facebook.
 *
 * Props:
 *  - url:   absolute or root-relative URL to share
 *  - title: title text used in the Twitter intent + plain text fallback
 *  - testIdPrefix: optional, e.g. "kid-share"
 */
export default function ShareBar({ url, title, testIdPrefix = "share" }) {
  const [copied, setCopied] = useState(false);

  // Resolve to an absolute production URL.
  // At runtime, prefer the actual current origin (correct on prod + preview).
  // At prerender time (puppeteer), window is undefined OR origin is localhost — fall back to SITE_URL.
  const runtimeOrigin = typeof window !== "undefined" ? window.location.origin : "";
  const isPrerenderHost = !runtimeOrigin || runtimeOrigin.includes("localhost") || runtimeOrigin.includes("127.0.0.1");
  const origin = isPrerenderHost ? SITE_URL : runtimeOrigin;
  const absoluteUrl = url.startsWith("http") ? url : `${origin}${url}`;

  const encodedUrl = encodeURIComponent(absoluteUrl);
  const encodedTitle = encodeURIComponent(title);
  const twitterHref = `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`;
  const facebookHref = `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(absoluteUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      /* clipboard blocked — silently no-op */
    }
  };

  return (
    <div
      className="mt-6 flex items-center gap-2 text-fc-muted"
      data-testid={`${testIdPrefix}-bar`}
    >
      <span className="overline mr-2" style={{opacity:0.7}}>Share</span>

      <a
        href={twitterHref}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Share on X / Twitter"
        className="w-9 h-9 border border-fc-border hover:border-fc-ink hover:text-fc-ink flex items-center justify-center transition-colors"
        data-testid={`${testIdPrefix}-twitter`}
      >
        <Twitter size={14} />
      </a>

      <a
        href={facebookHref}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Share on Facebook"
        className="w-9 h-9 border border-fc-border hover:border-fc-ink hover:text-fc-ink flex items-center justify-center transition-colors"
        data-testid={`${testIdPrefix}-facebook`}
      >
        <Facebook size={14} />
      </a>

      <button
        type="button"
        onClick={copy}
        aria-label={copied ? "Link copied" : "Copy link"}
        className="w-9 h-9 border border-fc-border hover:border-fc-ink hover:text-fc-ink flex items-center justify-center transition-colors"
        data-testid={`${testIdPrefix}-copy`}
      >
        {copied ? <Check size={14} className="text-fc-accent" /> : <Link2 size={14} />}
      </button>

      {copied && (
        <span className="overline text-fc-accent ml-1" data-testid={`${testIdPrefix}-copied`}>Link copied</span>
      )}
    </div>
  );
}
